import {
  Button,
  Icon,
  IconButton,
  Input,
  Select,
  SelectOption,
  StepperWC,
  Textarea,
} from '@pypestream/design-system';
import { FC, useEffect, useRef, useState } from 'react';

import { FileUploadSection } from '../components/modals/create-solution-modal/step-agent';
import { useSolutionWizardForm } from '../components/modals/create-solution-modal/use-solution-wizard-form';
import { useSolutionNameValidation } from '../hooks';
import knowledgeLogo from './design-agents/knowledge-agent.svg?url';
import type { SetupCopy } from './setup-copy';

/**
 * "Set it up step by step" (prototype `renderManual`): the old creation
 * screens, one at a time, in one card on the page — a title and model, what
 * the solution calls itself, its Knowledge Agent, and its tasks. Create goes
 * through the wizard's own `createSolution`, so this and the modal wizard
 * make exactly the same solution.
 */

interface Task {
  name: string;
  phrases: string;
  open: boolean;
}

const STEPS = [
  {
    title: 'Create a new solution',
  },
  {
    title: 'Solution name',
    sub: 'How the solution will refer to itself, e.g. Hi, I’m Emily your digital assistant.',
  },
  {
    title: 'Create a Knowledge Agent',
  },
  {
    title: 'Define tasks',
  },
];

/** The model types the wizard offers today. */
const MODEL_TYPES = ['gemini-2.5-flash'];

/** The wizard's model settings, unchanged: this form doesn't expose them. */
const MODEL_DEFAULTS = {
  temperature: 0.2,
  maxOutputTokens: 2048,
  topP: 0.8,
  topK: 0.2,
};

/** "Fill in an example": text only — documents are always the user's own. */
const EXAMPLE = {
  title: 'Harbor Mutual',
  intro: 'Hana, your insurance assistant',
  brand: 'Harbor Mutual',
  tone: 'Friendly and plain-spoken, with no insurance jargon. Short answers first, detail if they ask.',
  tasks: [
    {
      name: 'Policy questions',
      phrases:
        'What does my policy cover? | Is my deductible $500? | When is my payment due?',
      open: true,
    },
    {
      name: 'Claim status',
      phrases:
        'Where is my claim? | Check claim 48213 | Has my claim been approved?',
      open: true,
    },
    {
      name: 'Agent escalation',
      phrases: 'I want to escalate | I want to talk to a real human',
      open: true,
    },
  ],
};

const newTask = (): Task => ({ name: '', phrases: '', open: true });

// The design system's inputs are web components: their `input` event's
// target is the host element, which carries `value`.
const valueOf = (e: unknown) =>
  String((e as { target?: { value?: unknown } }).target?.value ?? '');

export const StepByStepForm: FC<{
  onBack: () => void;
  /** "Copy as a new solution": another solution's setup, to start from. */
  initial?: SetupCopy | null;
}> = ({ onBack, initial }) => {
  // The wizard's create path without its stepper: nothing here to move.
  const noStepper = useRef<StepperWC>(null);
  const {
    createSolution,
    credentials,
    isLoading,
    setIsLoading,
    isBotFlowError,
    uploadedFiles,
    addFile,
    removeFile,
  } = useSolutionWizardForm(noStepper);

  const [step, setStep] = useState(0);
  const [title, setTitle] = useState(initial?.title ?? '');
  const [credentialId, setCredentialId] = useState('');
  const [intro, setIntro] = useState(initial?.intro ?? '');
  const [brand, setBrand] = useState(initial?.brand ?? '');
  const [tone, setTone] = useState(initial?.tone ?? '');
  const [tasks, setTasks] = useState<Task[]>(() =>
    initial?.tasks.length
      ? initial.tasks.map((t) => ({ ...t, open: false }))
      : [newTask()]
  );
  const [failed, setFailed] = useState(false);
  // Only a create started here counts: the machine can still be parked in
  // `botFlowError` from an earlier attempt when this form opens.
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!attempt || !isBotFlowError) return;
    setIsLoading(false);
    setFailed(true);
  }, [attempt, isBotFlowError, setIsLoading]);

  const { isValidName, hasLengthError, validateSolutionName, getTag } =
    useSolutionNameValidation(title);

  // A provider picked for them when there's exactly one choice.
  useEffect(() => {
    if (!credentialId && credentials?.length === 1) {
      setCredentialId(credentials[0].id || '');
    }
  }, [credentials, credentialId]);

  const valid = [
    isValidName,
    !!intro.trim(),
    !!brand.trim(),
    tasks.length > 0 && tasks.every((t) => t.name.trim() && t.phrases.trim()),
  ][step];
  const last = step === STEPS.length - 1;

  const updateTask = (i: number, change: Partial<Task>) =>
    setTasks((ts) => ts.map((t, j) => (j === i ? { ...t, ...change } : t)));

  const create = () => {
    setFailed(false);
    createSolution({
      formData: {
        botName: title,
        type: 'supervisor',
        llmCredentialId: credentialId || undefined,
        supervisorName: intro.trim(),
        brandName: brand.trim(),
        tone: tone.trim(),
        tasks: tasks.map((t) => ({
          name: t.name.trim(),
          description: t.phrases.trim(),
        })),
        ...MODEL_DEFAULTS,
      },
      callback: () => undefined,
    });
    // After the send, so this render already sees the machine creating.
    setAttempt((n) => n + 1);
  };

  const next = () => {
    if (!valid || isLoading) return;
    if (last) create();
    else setStep((s) => s + 1);
  };

  const fillExample = () => {
    // A title they've typed is theirs; the example only fills a blank one.
    if (!title.trim()) setTitle(validateSolutionName(EXAMPLE.title));
    setIntro(EXAMPLE.intro);
    setBrand(EXAMPLE.brand);
    setTone(EXAMPLE.tone);
    setTasks(EXAMPLE.tasks.map((t) => ({ ...t })));
  };

  return (
    <div className="c-home-start" data-testid="bobcat-home-steps">
      <Button size="small" variant="ghost" onClick={onBack}>
        <Icon slot="prefix" name="arrow-left" size="xsmall" />
        Ways to start
      </Button>
      <form
        className="c-home-start__form c-home-steps"
        // Only Next advances: any design-system button in here (Back, a
        // task's icons) can submit the form, and that must never move a step.
        onSubmit={(e) => e.preventDefault()}
      >
        <ol className="c-home-steps__progress" aria-label="Setup steps">
          {STEPS.map((s, i) => (
            <li
              key={s.title}
              className={
                i < step ? 'is-done' : i === step ? 'is-now' : undefined
              }
              aria-current={i === step ? 'step' : undefined}
            >
              <span className="c-home-steps__pip-label">{s.title}</span>
            </li>
          ))}
        </ol>
        <div className="c-home-steps__top">
          <span className="c-home-steps__count">
            Step {step + 1} of {STEPS.length}
          </span>
          <button
            type="button"
            className="c-home-steps__example"
            data-testid="bobcat-home-steps-example"
            disabled={isLoading}
            onClick={fillExample}
          >
            <Icon name="stars" size="xsmall" />
            Fill in an example
          </button>
        </div>
        <h1 className="c-home-steps__title">
          {step === 2 && (
            <img src={knowledgeLogo} alt="" width={32} height={32} />
          )}
          {STEPS[step].title}
        </h1>
        {'sub' in STEPS[step] && (
          <p className="c-home-steps__sub">{STEPS[step].sub}</p>
        )}

        {step === 0 && (
          <>
            <label className="c-home-start__label" htmlFor="bobcat-steps-title">
              Solution title*
            </label>
            <Input
              id="bobcat-steps-title"
              test-id="bobcat-home-steps-title"
              variant="outlined"
              placeholder="Solution title"
              autofocus
              value={title}
              disabled={isLoading}
              hasError={hasLengthError}
              onInput={(e) => setTitle(validateSolutionName(valueOf(e)))}
            />
            <div className="c-home-start__rules">
              {getTag('1–100 characters', 'passedLengthCheck')}
              {getTag('Unique in this project', 'passedUniquenessCheck')}
            </div>
            <div className="c-home-start__row">
              <div>
                <span className="c-home-start__label">AI provider</span>
                {credentials?.length ? (
                  <Select
                    test-id="bobcat-home-steps-provider"
                    variant="outlined"
                    placeholder="Select a provider"
                    value={credentialId}
                    disabled={isLoading}
                    onChange={(e) => setCredentialId(valueOf(e))}
                  >
                    {credentials.map((c) => (
                      <SelectOption key={c.id} value={c.id || ''}>
                        {c.llmProvider?.name || 'Gemini'}
                      </SelectOption>
                    ))}
                  </Select>
                ) : (
                  <p className="c-home-start__hint">
                    No AI provider connected to this project yet. You can add
                    one later in the solution’s settings.
                  </p>
                )}
              </div>
              <div>
                <span className="c-home-start__label">Model type</span>
                <Select
                  test-id="bobcat-home-steps-model"
                  variant="outlined"
                  value={MODEL_TYPES[0]}
                  disabled={isLoading || !credentials?.length}
                >
                  {MODEL_TYPES.map((m) => (
                    <SelectOption key={m} value={m}>
                      {m}
                    </SelectOption>
                  ))}
                </Select>
              </div>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <label className="c-home-start__label" htmlFor="bobcat-steps-intro">
              Solution name*
            </label>
            <Input
              id="bobcat-steps-intro"
              test-id="bobcat-home-steps-intro"
              variant="outlined"
              placeholder="Emily, your digital assistant"
              autofocus
              value={intro}
              disabled={isLoading}
              onInput={(e) => setIntro(valueOf(e))}
            />
          </>
        )}

        {step === 2 && (
          <>
            <label className="c-home-start__label" htmlFor="bobcat-steps-brand">
              Brand name*
            </label>
            <Input
              id="bobcat-steps-brand"
              test-id="bobcat-home-steps-brand"
              variant="outlined"
              placeholder="The name customers know you by"
              autofocus
              value={brand}
              disabled={isLoading}
              onInput={(e) => setBrand(valueOf(e))}
            />
            <label className="c-home-start__label" htmlFor="bobcat-steps-tone">
              Tone
            </label>
            <Textarea
              id="bobcat-steps-tone"
              test-id="bobcat-home-steps-tone"
              variant="outlined"
              rows={3}
              placeholder="Friendly and plain-spoken, with no jargon"
              value={tone}
              disabled={isLoading}
              onInput={(e) => setTone(valueOf(e))}
            />
            <span className="c-home-start__label">Documents</span>
            <FileUploadSection
              uploadedFiles={uploadedFiles}
              addFile={addFile}
              removeFile={removeFile}
            />
          </>
        )}

        {step === 3 && (
          <>
            <ol className="c-home-steps__tasks">
              {tasks.map((t, i) => (
                // Tasks have no id; their order is their identity here.
                // eslint-disable-next-line react/no-array-index-key
                <li key={i} className="c-home-steps__task">
                  <div className="c-home-steps__task-head">
                    <Icon name="target" size="small" />
                    <span className="c-home-steps__task-title">
                      {t.name.trim() || 'New task'}
                    </span>
                    {tasks.length > 1 && (
                      <IconButton
                        name="delete"
                        variant="tertiary"
                        size="small"
                        aria-label={`Remove ${t.name.trim() || 'this task'}`}
                        disabled={isLoading}
                        onClick={() =>
                          setTasks((ts) => ts.filter((_, j) => j !== i))
                        }
                      />
                    )}
                    <IconButton
                      name={t.open ? 'chevron-up' : 'chevron-down'}
                      variant="tertiary"
                      size="small"
                      aria-expanded={t.open}
                      aria-label={t.open ? 'Collapse task' : 'Expand task'}
                      onClick={() => updateTask(i, { open: !t.open })}
                    />
                  </div>
                  {t.open && (
                    <div className="c-home-steps__task-body">
                      <label
                        className="c-home-start__label"
                        htmlFor={`bobcat-steps-task-${i}`}
                      >
                        Task name*
                      </label>
                      <Input
                        id={`bobcat-steps-task-${i}`}
                        test-id={`bobcat-home-steps-task-name-${i}`}
                        variant="outlined"
                        placeholder="Agent escalation"
                        value={t.name}
                        disabled={isLoading}
                        onInput={(e) => updateTask(i, { name: valueOf(e) })}
                      />
                      <label
                        className="c-home-start__label"
                        htmlFor={`bobcat-steps-phrases-${i}`}
                      >
                        Example phrases*
                      </label>
                      <Textarea
                        id={`bobcat-steps-phrases-${i}`}
                        test-id={`bobcat-home-steps-task-phrases-${i}`}
                        variant="outlined"
                        rows={2}
                        placeholder="I want to escalate | I want to talk to a real human"
                        value={t.phrases}
                        disabled={isLoading}
                        onInput={(e) => updateTask(i, { phrases: valueOf(e) })}
                      />
                      <span className="c-home-start__hint">
                        Separate phrases with |
                      </span>
                    </div>
                  )}
                </li>
              ))}
            </ol>
            <button
              type="button"
              className="c-home-steps__add"
              disabled={isLoading}
              onClick={() => setTasks((ts) => [...ts, newTask()])}
            >
              <span className="c-home-steps__add-dot">
                <Icon name="plus" size="xsmall" />
              </span>
              Add task
            </button>
          </>
        )}

        {failed && (
          <p className="c-home-start__error" role="alert">
            Couldn’t create the solution. Try again in a moment — if it keeps
            failing, the error is in the notifications.
          </p>
        )}
        <div className="c-home-start__actions">
          {step > 0 && (
            <Button
              variant="secondary"
              type="button"
              disabled={isLoading}
              onClick={() => setStep((s) => s - 1)}
            >
              Back
            </Button>
          )}
          <Button
            test-id={
              last ? 'bobcat-home-steps-create' : 'bobcat-home-steps-next'
            }
            // Not a submit button: the design system's button submits its
            // form as well as firing onClick, so one click would advance twice
            // (and, with later steps already filled, run straight to Create).
            type="button"
            disabled={!valid || isLoading}
            onClick={next}
          >
            {last ? (isLoading ? 'Creating…' : 'Create') : 'Next'}
          </Button>
        </div>
      </form>
    </div>
  );
};
